import { useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useAppSelector } from "store/index"
import { userSelectors } from "store/apis/userAPI"
import {
  useGetAuctionCurrentQuery,
  useGetAuctionHistoryQuery,
  AUCTION_STATUS,
  auctionActiveStatuses
} from "store/apis/auctionAPI"
import {
  incrementCurrentAuctionIndex,
  decrementCurrentAuctionIndex,
  setCurrentAuctionIndex,
  setIsLastAuction
} from "store/slices/auctionSlice"
import { useModals } from "context/modals/hooks/useModals"

export const useAuction = () => {
  const { openModal } = useModals()
  const { pathname } = useLocation()
  const dispatch = useDispatch()

  const { currentAuctionIndex, isLastAuction } = useAppSelector(
    (state) => state.auction
  )
  const { data: user } = useSelector(userSelectors.selectCurrentUser)

  const setAuctionIndex = (index: number) => {
    dispatch(setCurrentAuctionIndex(index))
  }

  const nextAuction = () => {
    dispatch(decrementCurrentAuctionIndex())
  }
  const prevAuction = () => {
    dispatch(incrementCurrentAuctionIndex())
  }

  const setLastAuction = (isLast: boolean) => {
    dispatch(setIsLastAuction(isLast))
  }

  const {
    data,
    isLoading: isCurrentAuctionLoading,
    refetch: refetchCurrentAuction
  } = useGetAuctionCurrentQuery({
    userId: user?._id
  })

  const {
    data: history,
    isLoading: isHistoryLoading,
    refetch: refetchHistory
  } = useGetAuctionHistoryQuery(
    {
      page: currentAuctionIndex,
      limit: 1
    },
    {
      skip: currentAuctionIndex === 0
    }
  )

  const { data: nextHistory } = useGetAuctionHistoryQuery(
    {
      page: currentAuctionIndex + 1,
      limit: 1
    },
    {
      skip: currentAuctionIndex === 0
    }
  )

  const auctionData = useMemo(() => {
    if (currentAuctionIndex > 0) {
      return history?.items?.[0] || null
    }

    return data || null
  }, [currentAuctionIndex, data, history])

  const isLoading = useMemo(() => {
    if (currentAuctionIndex > 0) {
      return isHistoryLoading
    }

    return isCurrentAuctionLoading
  }, [currentAuctionIndex, isHistoryLoading, isCurrentAuctionLoading])

  const refetch = useMemo(() => {
    if (currentAuctionIndex > 0) {
      return refetchHistory
    }

    return refetchCurrentAuction
  }, [currentAuctionIndex, refetchHistory, refetchCurrentAuction])

  const isActive = useMemo(() => {
    if (!auctionData?.auction) return false

    return auctionActiveStatuses.includes(auctionData?.auction?.status)
  }, [auctionData])

  const isFinished = useMemo(() => {
    if (!auctionData?.auction) return false
    return auctionData?.auction?.status === AUCTION_STATUS.FINISHED
  }, [auctionData])

  const isHistory = useMemo(() => {
    return currentAuctionIndex > 0
  }, [currentAuctionIndex])

  useEffect(() => {
    if (!nextHistory?.items?.[0]) {
      setLastAuction(true)
    } else {
      setLastAuction(false)
    }
  }, [nextHistory])

  useEffect(() => {
    if (!data?.auction) return

    if (data?.auction?.status === AUCTION_STATUS.FINISHED) {
      const isAuctionPage = pathname.includes("auction")
      const wasBidded = data.lots.some((currentLot) =>
        Boolean(currentLot.lot.lastBidder)
      )

      if (!isAuctionPage && !wasBidded) return

      openModal("auctionFinished")
    }
  }, [data])

  return {
    data: auctionData,
    isLoading,
    isActive,
    refetch,
    currentAuctionIndex,
    nextAuction,
    prevAuction,
    setAuctionIndex,
    isFinished,
    isHistory,
    isLastAuction
  }
}
